import { Table } from '@tanstack/react-table';

import { ApprovalTimeEntry } from './time-tracking-approvals';

/**
 * A pending inline edit on one worklog row. Each field is independently optional: editing only the
 * bill input must not overwrite the invoice value with a stale render snapshot.
 * `approvalNotes: null` is a real edit ("the user cleared the notes"), which is why every consumer
 * compares it against `undefined` rather than truthiness.
 */
export interface EditedEntryValues {
  approvedForInvoiceHours?: number;
  approvedForBillHours?: number;
  approvalNotes?: string | null;
}

/**
 * What the approvals table hands its column definitions through TanStack's `meta`.
 *
 * A type alias, not an interface: `TableMeta` is declared with an index signature (see
 * `src/types/tanstack-table.d.ts`) and only aliases are assignable to it.
 */
export type ApprovalsTableMeta = {
  loadingRowIds: Set<string>;
  /**
   * Commits an inline edit. The whole entry is passed (not just its id) because the table
   * derives the row's ancestors from it — an edit must not steal the selection from a
   * project/member row that already covers the entry.
   */
  onApprovedForInvoiceHoursChange: (entry: ApprovalTimeEntry, hours: number) => void;
  onApprovedForBillHoursChange: (entry: ApprovalTimeEntry, hours: number) => void;
  onApprovalNotesChange: (entry: ApprovalTimeEntry, notes: string | null) => void;
  /**
   * Reports one approved input as unparseable (`rawValue`) or parseable again (`null`).
   *
   * Tracked in the store rather than the cell because three things outlive the input: the
   * action footer and the row's approve button must refuse while it is broken, the member and
   * project rows must warn about it, and the text must survive collapsing the row.
   */
  onApprovedHoursValidityChange: (
    entry: ApprovalTimeEntry,
    field: ApprovedHoursField,
    rawValue: string | null
  ) => void;
};

/** Which of the two approved values an input edits. */
export enum ApprovedHoursField {
  Invoice = 'invoice',
  Bill = 'bill',
}

/**
 * One approved-hours input whose text does not parse.
 *
 * Carries the ancestor ids and the raw text on purpose: the row can be collapsed out of the
 * tree at any moment, and we still need to (a) warn on its member and project rows and
 * (b) put the user's text back when they expand it again.
 */
export interface InvalidApprovedInput {
  entryId: number;
  projectId: number;
  memberId: number;
  field: ApprovedHoursField;
  /** Exactly what the user typed — restored verbatim so their correction is never lost. */
  rawValue: string;
}

/** Invalid approved inputs keyed by `{entryId}:{field}` — see `invalidApprovedInputKey`. */
export type InvalidApprovedInputs = Record<string, InvalidApprovedInput>;

/**
 * The single place that narrows the approvals table's `meta`. Undefined only if a column is
 * ever rendered by a table that doesn't provide it, so callers still guard.
 */
export const getApprovalsTableMeta = <TData>(table: Table<TData>): ApprovalsTableMeta | undefined =>
  table.options.meta as ApprovalsTableMeta | undefined;
